import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { fetchCurrentCustomer, logout as logoutRequest, type Customer } from '../lib/api';

/**
 * Who is signed in.
 *
 * The session itself lives in an httpOnly cookie the browser attaches automatically.
 * This context holds only what the server tells us about it -- an email address -- and
 * never a token: there is deliberately nothing here that JavaScript could leak.
 *
 * "Signed in" therefore means "the server said so on the last check", not "we have a
 * credential". Every protected read is authorised again server-side, so a stale value
 * here can cause a wasted request but can never grant access.
 */

export type SessionState =
  | { status: 'loading' }
  | { status: 'authenticated'; customer: Customer }
  | { status: 'anonymous' };

interface SessionContextValue {
  state: SessionState;
  /** Re-check with the server, e.g. straight after verifying a sign-in link. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const customer = await fetchCurrentCustomer(signal);
      setState(customer ? { status: 'authenticated', customer } : { status: 'anonymous' });
    } catch {
      // A failed check means we cannot claim anyone is signed in. Treating it as
      // anonymous is the safe direction: the worst case is an unnecessary sign-in.
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      // Regardless of what the request did, this browser stops claiming a session. The
      // server-side revocation is what actually kills it.
      setState({ status: 'anonymous' });
    }
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ state, refresh: () => load(), signOut }),
    [state, load, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within a SessionProvider');
  return context;
}
